/**
 * Assign Risk Level Dialog Component
 * Displays a confirmation dialog for bulk assigning risk levels to customers
 * Shows counts of risk level changes
 */

import React from "react";
import { AlertCircle, CheckCircle2, Users, TrendingUp } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import type { RiskLevelOption } from "@/hooks/useRiskLevelsData";
import { type RiskLevelCode, getRiskTextClassName } from "@/lib/risk-level";

interface Customer {
  id: string;
  name: string;
  accountNumber: string;
  currency: "USD" | "EUR" | "GBP" | "CAD" | "AUD";
  outstanding: number;
  overdue: number;
  creditLimit: number;
  dso: number;
  risk: RiskLevelCode;
  lastPayment: string;
  contact: string;
  phone: string;
  address: string;
  paymentTerms: string;
  collectionStrategyId: string;
}

interface AssignRiskLevelDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedCustomers: Customer[];
  riskLevels: RiskLevelOption[];
  onConfirm: (riskLevelId: number | null) => void;
}

const getRiskLevelColor = (code: string): string => getRiskTextClassName(code as RiskLevelCode);

export function AssignRiskLevelDialog({
  open,
  onOpenChange,
  selectedCustomers,
  riskLevels,
  onConfirm,
}: Readonly<AssignRiskLevelDialogProps>) {
  const [selectedRiskLevelId, setSelectedRiskLevelId] = React.useState<string>("");

  // Preselect risk level when a single customer is selected
  React.useEffect(() => {
    if (open && selectedCustomers.length === 1) {
      const customerRisk = selectedCustomers[0].risk;
      if (customerRisk === "none") {
        setSelectedRiskLevelId("none");
      } else {
        const matchingLevel = riskLevels.find((x) => x.code.toLowerCase() === customerRisk);
        setSelectedRiskLevelId(matchingLevel ? String(matchingLevel.id) : "");
      }
    } else if (open) {
      setSelectedRiskLevelId("");
    }
  }, [open, selectedCustomers, riskLevels]);

  // Calculate counts by current risk level
  const totalSelected = selectedCustomers.length;
  const riskBreakdown = riskLevels.map((rl) => ({
    code: rl.code,
    name: rl.name,
    count: selectedCustomers.filter((c) => c.risk === rl.code.toLowerCase()).length,
  }));

  // Get selected risk level details (null when "None" is selected)
  const isNoneSelected = selectedRiskLevelId === "none";
  const selectedRisk = isNoneSelected ? null : riskLevels.find((x) => String(x.id) === selectedRiskLevelId);
  const selectedRiskName = isNoneSelected ? "None" : selectedRisk?.name;

  // Calculate how many will change
  const selectedRiskCode = isNoneSelected ? "none" : (selectedRisk?.code.toLowerCase() ?? "");
  const willChange = selectedRiskLevelId
    ? selectedCustomers.filter((c) => c.risk !== selectedRiskCode).length
    : 0;
  const willRemainSame = totalSelected - willChange;

  const handleConfirm = () => {
    if (isNoneSelected) {
      onConfirm(null);
      setSelectedRiskLevelId("");
      onOpenChange(false);
    } else if (selectedRiskLevelId && selectedRisk) {
      onConfirm(selectedRisk.id);
      setSelectedRiskLevelId("");
      onOpenChange(false);
    }
  };

  const handleCancel = () => {
    setSelectedRiskLevelId("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <TrendingUp className="h-5 w-5 text-primary" />
            Assign Risk Level
          </DialogTitle>
          <DialogDescription>
            Select a risk level to assign to the selected customer accounts.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Risk Level Selection */}
          <div className="space-y-2">
            <Label htmlFor="risk-select">Risk Level</Label>
            <Select value={selectedRiskLevelId} onValueChange={setSelectedRiskLevelId}>
              <SelectTrigger id="risk-select">
                <SelectValue placeholder="Select a risk level..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${getRiskLevelColor("none")}`}>None</span>
                  </div>
                </SelectItem>
                {riskLevels.map((x) => (
                  <SelectItem key={x.id} value={String(x.id)}>
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${getRiskLevelColor(x.code)}`}>{x.name} Risk</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedRisk?.description && (
              <p className="text-sm text-muted-foreground mt-2">
                {selectedRisk.description}
              </p>
            )}
          </div>

          <Separator />

          {/* Impact Summary */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-muted-foreground" />
              <h4 className="font-semibold text-sm">Impact Summary</h4>
            </div>

            <div className="grid gap-3">
              {/* Total Selected */}
              <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm font-medium">Total Selected</span>
                </div>
                <Badge variant="secondary" className="text-base font-semibold">
                  {totalSelected}
                </Badge>
              </div>

              {/* Current Risk Breakdown */}
              <div className="p-3 rounded-lg bg-muted/30 border border-muted">
                <div className="text-sm font-medium mb-2">Current Risk Distribution</div>
                <div className={`grid grid-cols-${riskBreakdown.length} gap-2`}>
                  {riskBreakdown.map((rb) => (
                    <div key={rb.code} className="flex flex-col items-center p-2 rounded bg-muted/50 border border-muted">
                      <span className={`text-xs font-medium ${getRiskLevelColor(rb.code)}`}>{rb.name}</span>
                      <span className="text-lg font-bold">{rb.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Changes Preview */}
              {selectedRiskLevelId && willChange > 0 && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-500" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-green-900 dark:text-green-100">
                        Risk Level Changes
                      </span>
                      <span className="text-xs text-green-700 dark:text-green-400">
                        Customers that will be updated to {isNoneSelected ? "None" : `${selectedRiskName} Risk`}
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-green-600 hover:bg-green-700 text-white text-base font-semibold">
                    {willChange}
                  </Badge>
                </div>
              )}

              {/* No Changes */}
              {selectedRiskLevelId && willRemainSame > 0 && (
                <div className="flex items-center justify-between p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-500" />
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                        Already at Selected Level
                      </span>
                      <span className="text-xs text-blue-700 dark:text-blue-400">
                        Customers already marked as {isNoneSelected ? "None" : `${selectedRiskName} Risk`}
                      </span>
                    </div>
                  </div>
                  <Badge className="bg-blue-600 hover:bg-blue-700 text-white text-base font-semibold">
                    {willRemainSame}
                  </Badge>
                </div>
              )}
            </div>

            {/* Information Note */}
            <div className="flex gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-900">
              <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-500 mt-0.5 flex-shrink-0" />
              <p className="text-xs text-blue-900 dark:text-blue-100">
                <strong>Note:</strong> This action will immediately assign the selected risk level 
                to all {totalSelected} customer{totalSelected !== 1 ? "s" : ""}. 
                {willChange > 0 && (
                  <> {willChange} customer{willChange !== 1 ? "s" : ""} will have their risk level updated.</>
                )}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!selectedRiskLevelId}
            variant={isNoneSelected ? "outline" : "default"}
            className="gap-2"
          >
            <CheckCircle2 className="h-4 w-4" />
            Confirm Assignment
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
